import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { Logger } from '@utils/logger';
import { DocumentNode } from '@apollo/client/core';
import { Apollo } from 'apollo-angular';
import { map } from 'rxjs/operators';
import { MapperFactory } from '@products/utils/mappers/mapper-factory';
import { FundListing, Product } from '@models';
import { ProductsMapper } from '@products/utils/mappers/product.type.mapper';
import { MapperParams } from '@products/utils/mappers/type.mapper';
import { ProductDTO } from '@types';
import {
  FundDataServiceParams,
  GraphQLFundDataService,
} from '@products/services/graphql-fund-data.service';
import { SiteConfigService } from '@services/site-config.service';

const logger = Logger.getLogger('LiteratureFundDataService');

@Injectable({
  providedIn: 'root',
})
export class LiteratureFundDataService extends GraphQLFundDataService {
  constructor(
    apollo: Apollo,
    siteConfigService: SiteConfigService,
    private mapperFactory: MapperFactory
  ) {
    super(apollo, siteConfigService);
  }

  public register(
    query: DocumentNode,
    fundDataServiceParams: FundDataServiceParams,
    activeList?: string[],
    overrideFlag?: boolean
  ): Observable<any> {
    return super.register(query, this.getVariables(fundDataServiceParams)).pipe(
      map((rawPpssData) => {
        const rawPpssFunds: ProductDTO[] = [
          ...rawPpssData.data.etf,
          ...rawPpssData.data.mf,
          ...rawPpssData.data.mmf,
          ...rawPpssData.data.vip,
          ...rawPpssData.data._529,
          ...rawPpssData.data.cef,
          ...rawPpssData.data.sma,
          ...rawPpssData.data.tif,
          ...rawPpssData.data.if,
        ];
        const mappperParams: MapperParams = {
          config: this.siteConfigService,
          includeSoftClosed: true,
        };
        const products: Product[] = this.mapperFactory
          .createMapper(ProductsMapper, mappperParams)
          .toDomain(rawPpssFunds, activeList, overrideFlag);

        return new FundListing(products);
      })
    );
  }

  /**
   * Return required variables value to graphql query.
   * @param fundDataServiceParams params required for graphql query
   */
  private getVariables(
    fundDataServiceParams: FundDataServiceParams
  ): Record<string, any> {
    return {
      countrycode: this.getCountry(),
      languagecode: this.getLanguage(),
      dateFormat: this.getDateFormat(),
      productTypemf: 'mf',
      productTypeetf: 'etf',
      productType529: '529',
      productTypecef: 'cef',
      productTypemmf: 'mmf',
      productTypevip: 'variable-insurance',
      productTypesma: 'sma',
      productTypetif: 'tif',
      productTypeif: 'if',
    };
  }
}
